import { AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';
import type { AngleValidation } from '@/types/concept';
import { AngleValidationStatus as ANGLE_VALIDATION_STATUS } from '@/shared/constants/status';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { Badge } from '@/shared/ui/shadcn/badge';

interface AngleValidationStatusProps {
  validation?: AngleValidation;
  showDetails?: boolean;
  className?: string;
}

export function AngleValidationStatus({
  validation,
  showDetails = false,
  className = '',
}: AngleValidationStatusProps) {
  if (!validation) {
    return null;
  }

  const isFinished = validation.is_in_progress === false;
  const isValid = validation.status === ANGLE_VALIDATION_STATUS.VALID;

  const getStatusIcon = () => {
    if (
      validation.status === ANGLE_VALIDATION_STATUS.PENDING ||
      validation.status === ANGLE_VALIDATION_STATUS.PROCESSING
    ) {
      return <Clock className="h-4 w-4 animate-pulse" />;
    }

    if (isFinished) {
      if (!isValid) {
        return <XCircle className="h-4 w-4" />;
      }
      if (validation.recommendations && validation.recommendations.length > 0) {
        return <AlertCircle className="h-4 w-4" />;
      }
      return <CheckCircle className="h-4 w-4" />;
    }

    return null;
  };

  const getStatusVariant = ():
    | 'default'
    | 'secondary'
    | 'destructive'
    | 'outline' => {
    if (
      validation.status === ANGLE_VALIDATION_STATUS.PENDING ||
      validation.status === ANGLE_VALIDATION_STATUS.PROCESSING
    ) {
      return 'secondary';
    }

    if (isFinished) {
      if (!isValid) {
        return 'destructive';
      }
      if (validation.recommendations && validation.recommendations.length > 0) {
        return 'outline';
      }
      return 'default';
    }

    return 'secondary';
  };

  const getStatusText = () => {
    if (
      validation.status === ANGLE_VALIDATION_STATUS.PENDING ||
      validation.status === ANGLE_VALIDATION_STATUS.PROCESSING
    ) {
      return 'Analyzing...';
    }

    if (isFinished) {
      if (!isValid) {
        return 'Invalid';
      }
      if (validation.recommendations && validation.recommendations.length > 0) {
        return 'Has recommendations';
      }
      return 'Valid';
    }

    return '';
  };

  return (
    <div className={`${className}`}>
      <Badge
        variant={getStatusVariant()}
        className={`inline-flex items-center gap-1.5 ${
          isFinished &&
          isValid &&
          validation.recommendations?.length === 0
            ? 'bg-primary/10 text-primary hover:bg-primary/20'
            : ''
        }`}
      >
        {getStatusIcon()}
        <span>{getStatusText()}</span>
      </Badge>

      {showDetails && isFinished && (
          <div className="mt-2 text-form-help">
            {validation.angle_short_name && (
              <p className="text-muted-foreground mb-1">
                {validation.angle_short_name}
              </p>
            )}

            {validation.recommendations && (
              <Alert className="mt-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {validation.recommendations}
                </AlertDescription>
              </Alert>
            )}

            {validation.building_type && (
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge variant="secondary" className="text-chip">
                  {validation.building_type}
                </Badge>
                {validation.source_building_style && (
                  <Badge variant="secondary" className="text-chip">
                    {validation.source_building_style}
                  </Badge>
                )}
                {validation.form_of_source && (
                  <Badge variant="secondary" className="text-chip">
                    {validation.form_of_source}
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}
    </div>
  );
}
